import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Trophy, Search, ChevronDown, ChevronUp, DollarSign } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const MONTHS = [
  { v: 1, label: "January" }, { v: 2, label: "February" }, { v: 3, label: "March" },
  { v: 4, label: "April" }, { v: 5, label: "May" }, { v: 6, label: "June" },
  { v: 7, label: "July" }, { v: 8, label: "August" }, { v: 9, label: "September" },
  { v: 10, label: "October" }, { v: 11, label: "November" }, { v: 12, label: "December" },
];

export default function LeaguesPage({ basePath = "/admin", canMutate = true }) {
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [seasonStartMonth, setSeasonStartMonth] = useState(8);
  const [country, setCountry] = useState("");
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  // Phase 13.5 — manual transfer-fee ceilings modal (10 inputs in millions €).
  const POSITIONS = ["GK", "RB", "CB", "LB", "DM", "CM", "AM", "RW", "LW", "CF"];
  const POSITION_LABELS = {
    GK: "GK · Goalkeeper",
    RB: "RB · Right Back",
    CB: "CB · Center Back",
    LB: "LB · Left Back",
    DM: "DM · Defensive Mid",
    CM: "CM · Central Mid",
    AM: "AM · Attacking Mid",
    RW: "RW · Right Winger",
    LW: "LW · Left Winger",
    CF: "CF · Centre Forward",
  };
  const blankCeilings = POSITIONS.reduce((acc, p) => ({ ...acc, [p]: "" }), {});
  const [ceilingsOpen, setCeilingsOpen] = useState(false);
  const [ceilingsLeague, setCeilingsLeague] = useState(null);
  const [ceilings, setCeilings] = useState(blankCeilings);
  const [ceilingsLoading, setCeilingsLoading] = useState(false);
  const [ceilingsError, setCeilingsError] = useState("");
  const [ceilingsSaving, setCeilingsSaving] = useState(false);

  const openCeilings = async (l) => {
    setCeilingsLeague(l);
    setCeilingsOpen(true);
    setCeilings(blankCeilings);
    setCeilingsError("");
    setCeilingsLoading(true);
    try {
      const { data } = await api.get(`/leagues/${l.id}/transfer-records`);
      // Stored as integer EUR; render as millions with sensible decimals.
      const m = {};
      for (const p of POSITIONS) {
        const v = data?.[p];
        if (v == null || v === 0) {
          m[p] = "";
        } else {
          const inM = v / 1_000_000;
          // Trim trailing zeros; e.g. 17.87 → "17.87", 13 → "13", 0.3 → "0.3".
          m[p] = Number.isFinite(inM) ? String(parseFloat(inM.toFixed(4))) : "";
        }
      }
      setCeilings(m);
    } catch (e) {
      setCeilingsError(formatApiError(e));
    } finally {
      setCeilingsLoading(false);
    }
  };

  const saveCeilings = async () => {
    setCeilingsError("");
    // Validate: all 10 filled with non-negative number.
    const body = {};
    for (const p of POSITIONS) {
      const raw = (ceilings[p] ?? "").toString().trim();
      if (raw === "") {
        setCeilingsError("All 10 positions must be filled with a non-negative number.");
        return;
      }
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) {
        setCeilingsError("All 10 positions must be filled with a non-negative number.");
        return;
      }
      body[p] = n;
    }
    setCeilingsSaving(true);
    try {
      await api.patch(`/leagues/${ceilingsLeague.id}/transfer-records`, body);
      toast.success("Transfer ceilings saved");
      setCeilingsOpen(false);
    } catch (e) {
      setCeilingsError(formatApiError(e));
    } finally {
      setCeilingsSaving(false);
    }
  };

  // Phase 8.3: sort by FBM League Score; default desc (strongest first).
  const [sortBy, setSortBy] = useState("score");
  const [sortDir, setSortDir] = useState("desc");

  const sortedLeagues = useMemo(() => {
    const out = [...leagues];
    const keyFn = sortBy === "score" ? (l) => l.fbm_league_score : (l) => l.name;
    const cmp = (a, b) => {
      const av = keyFn(a);
      const bv = keyFn(b);
      const aNull = av == null;
      const bNull = bv == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;       // nulls always last
      if (bNull) return -1;
      if (typeof av === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    };
    out.sort(cmp);
    return out;
  }, [leagues, sortBy, sortDir]);

  const onSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortDir(key === "score" ? "desc" : "asc");
    }
  };

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/leagues", { params: query ? { q: query } : {} });
      setLeagues(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const openCreate = () => { setEditing(null); setName(""); setSeasonStartMonth(8); setCountry(""); setDialogOpen(true); };
  const openEdit = (l) => { setEditing(l); setName(l.name); setSeasonStartMonth(l.season_start_month || 8); setCountry(l.country || ""); setDialogOpen(true); };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        season_start_month: seasonStartMonth,
        country: country.trim() || null,
      };
      if (editing) {
        await api.patch(`/leagues/${editing.id}`, payload);
        toast.success("League updated");
      } else {
        await api.post("/leagues", payload);
        toast.success("League created");
      }
      setDialogOpen(false); await load(q);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/leagues/${toDelete.id}`);
      toast.success("League deleted"); setToDelete(null); await load(q);
    } catch (err) { toast.error(formatApiError(err)); setToDelete(null); }
  };

  return (
    <div data-testid="leagues-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Competitions</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight">Leagues</h1>
          <p className="text-muted-foreground mt-2 max-w-lg">Domestic, continental and cup competitions tracked on the platform.</p>
        </div>
        <Button onClick={openCreate} className={`h-11 rounded-none font-display uppercase tracking-[0.18em] ${canMutate ? "" : "hidden"}`} data-testid="create-league-button">
          <Plus className="h-4 w-4 mr-2" /> New League
        </Button>
      </header>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leagues…" className="pl-9" data-testid="leagues-search-input" />
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <Table data-testid="leagues-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Name</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Country</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Season starts</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Teams</TableHead>
              <TableHead
                className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right cursor-pointer select-none"
                onClick={() => onSort("score")}
                data-testid="sort-fbm-league-score"
              >
                <span className="inline-flex items-center gap-1 justify-end w-full">
                  FBM League Score
                  {sortBy === "score" ? (
                    sortDir === "asc"
                      ? <ChevronUp className="h-3.5 w-3.5" data-testid="sort-fbm-league-score-asc" />
                      : <ChevronDown className="h-3.5 w-3.5" data-testid="sort-fbm-league-score-desc" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 opacity-25" />
                  )}
                </span>
              </TableHead>
              {canMutate && <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={canMutate ? 6 : 5} className="px-6 py-12 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : sortedLeagues.length === 0 ? (
              <TableRow><TableCell colSpan={canMutate ? 6 : 5} className="px-6 py-16 text-center">
                <Trophy className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                <div className="font-display uppercase tracking-wider">No leagues yet</div>
              </TableCell></TableRow>
            ) : (
              sortedLeagues.map((l) => (
                <TableRow
                  key={l.id}
                  className="tr-fade border-border hover:bg-muted/30 cursor-pointer"
                  data-testid={`league-row-${l.id}`}
                  onClick={() => navigate(`${basePath}/leagues/${l.id}`)}
                >
                  <TableCell className="px-6 py-4 font-medium">
                    <Link to={`${basePath}/leagues/${l.id}`} className="hover:underline" data-testid={`league-link-${l.id}`} onClick={(e) => e.stopPropagation()}>{l.name}</Link>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-muted-foreground text-sm" data-testid={`league-country-${l.id}`}>
                    {l.country || "—"}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-muted-foreground" data-testid={`league-ssm-${l.id}`}>
                    {(MONTHS.find((m) => m.v === (l.season_start_month || 8)) || {}).label}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right font-mono text-sm" data-testid={`league-team-count-${l.id}`}>
                    {l.team_count ?? 0}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right font-mono text-lg tabular-nums" data-testid={`league-score-${l.id}`}>
                    {l.fbm_league_score == null
                      ? <span className="text-muted-foreground text-base">—</span>
                      : Math.ceil(Number(l.fbm_league_score))}
                  </TableCell>
                  {canMutate && (
                    <TableCell className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(l); }} data-testid={`edit-league-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openCeilings(l); }} data-testid={`ceilings-league-${l.id}`} title="Transfer fee ceilings"><DollarSign className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setToDelete(l); }} data-testid={`delete-league-${l.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="league-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">{editing ? "Edit League" : "New League"}</DialogTitle>
            <DialogDescription>{editing ? "Update the league name." : "Create a new competition."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="league-name">Name</Label>
              <Input id="league-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="league-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Season starts in</Label>
              <Select value={String(seasonStartMonth)} onValueChange={(v) => setSeasonStartMonth(parseInt(v, 10))}>
                <SelectTrigger data-testid="league-ssm-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m) => (
                    <SelectItem key={m.v} value={String(m.v)} data-testid={`league-ssm-option-${m.v}`}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Determines how match dates are bucketed into seasons (e.g. August → 2024/25).</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="league-country">Country (optional)</Label>
              <Input
                id="league-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="e.g. Netherlands"
                data-testid="league-country-input"
              />
              <p className="text-xs text-muted-foreground">Free-text. Used for display and as the default country for teams created from this league's page.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="league-save-button">{saving ? "Saving…" : editing ? "Save changes" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete league?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete <strong>{toDelete?.name}</strong>. Teams referencing it must be detached first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-league-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 13.5 — Transfer-fee ceilings modal. */}
      <Dialog open={ceilingsOpen} onOpenChange={(o) => { if (!o) setCeilingsOpen(false); }}>
        <DialogContent className="sm:max-w-lg" data-testid="ceilings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">
              Transfer fee ceilings — {ceilingsLeague?.name}
            </DialogTitle>
            <DialogDescription>
              Highest transfer fee per primary position. Values in millions of euros
              (e.g. <span className="font-mono">40</span> = €40M, <span className="font-mono">0.3</span> = €300K).
            </DialogDescription>
          </DialogHeader>
          {ceilingsLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground" data-testid="ceilings-loading">
              Loading…
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="ceilings-grid">
              {POSITIONS.map((p) => (
                <div key={p} className="space-y-1">
                  <Label htmlFor={`ceil-${p}`} className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                    {POSITION_LABELS[p]}
                  </Label>
                  <Input
                    id={`ceil-${p}`}
                    type="number"
                    step="0.1"
                    min="0"
                    value={ceilings[p]}
                    onChange={(e) => setCeilings((prev) => ({ ...prev, [p]: e.target.value }))}
                    data-testid={`ceil-input-${p}`}
                  />
                </div>
              ))}
            </div>
          )}
          {ceilingsError && (
            <p className="text-sm text-rose-500" data-testid="ceilings-error">{ceilingsError}</p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCeilingsOpen(false)} data-testid="ceilings-cancel">
              Cancel
            </Button>
            <Button onClick={saveCeilings} disabled={ceilingsLoading || ceilingsSaving} data-testid="ceilings-save">
              {ceilingsSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
